import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend, Area, AreaChart,
} from "recharts";
import { Globe2, TrendingUp, Users2, DollarSign, Skull, Scale, Radio, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import MapView from "@/components/MapView";

const CYAN     = "#00D4FF";
const CRIMSON  = "#C1121F";
const PAPER    = "#E7E1D5";
const GUN      = "#84929F";
const DEEP     = "#168AAD";
const CHART_COLORS = [CYAN, CRIMSON, DEEP, PAPER, GUN];

const TOOLTIP_STYLE = {
  background: "#080B0F",
  border: "1px solid rgba(0,212,255,0.35)",
  borderRadius: 2,
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontSize: 11,
  color: "#F4F7F8",
};
const AXIS_STYLE = { stroke: "rgba(255,255,255,0.2)", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" };

// Global Organized Crime Index 2023 — top criminality scores
const HOTSPOTS = [
  { name: "Myanmar",  lat: 21.9,  lng: 95.9,   value: 8.15, detail: "Highest criminality globally" },
  { name: "Colombia", lat: 4.7,   lng: -74.1,  value: 7.75, detail: "Cocaine trafficking hub" },
  { name: "Mexico",   lat: 23.6,  lng: -102.5, value: 7.57, detail: "Cartel activity" },
  { name: "Paraguay", lat: -23.4, lng: -58.4,  value: 7.52, detail: "Contraband corridor" },
  { name: "Nigeria",  lat: 9.1,   lng: 8.7,    value: 7.28, detail: "Financial crime & trafficking" },
  { name: "DRC",      lat: -4.0,  lng: 21.8,   value: 7.35, detail: "Conflict-linked crime" },
  { name: "Libya",    lat: 26.3,  lng: 17.2,   value: 7.13, detail: "Trafficking gateway" },
  { name: "Afghanistan", lat: 33.9, lng: 67.7, value: 7.07, detail: "Opium production" },
  { name: "Iran",     lat: 32.4,  lng: 53.6,   value: 6.98, detail: "Regional trafficking" },
  { name: "Russia",   lat: 61.5,  lng: 105.3,  value: 6.83, detail: "Financial & cybercrime" },
];

// Lens filters — highlight relevant modules
const LENSES = [
  { id: "all",       label: "ALL SIGNALS" },
  { id: "cyber",     label: "CYBERCRIME" },
  { id: "traffic",   label: "TRAFFICKING" },
  { id: "aml",       label: "MONEY LAUNDERING" },
  { id: "regional",  label: "REGIONAL" },
];

const MODULE_LENS = {
  "cybercrime-trend": "cyber",
  "traffic-forms": "traffic",
  "laundering": "aml",
  "regions": "regional",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [lens, setLens] = useState("all");

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);

  if (!stats) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24" data-testid="dashboard-loading">
        <div className="flex items-center gap-3 text-white/60 font-mono text-xs tracking-[0.36em]">
          <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
          <span className="ml-2">LOADING GLOBAL RESEARCH MAP…</span>
        </div>
      </div>
    );
  }

  const kpis = [
    { icon: Globe2,     label: "Global Crime Index", val: stats.global_crime_index.value.toFixed(2), unit: "GOCI 2023",   accent: PAPER  },
    { icon: TrendingUp, label: "Cybercrime Cost",     val: `$${stats.cybercrime_cost_usd_trn}T`,     unit: "annual, 2026", accent: CYAN   },
    { icon: Users2,     label: "Trafficking Victims", val: stats.trafficking_victims_detected.toLocaleString(), unit: "detected · UNODC", accent: CRIMSON },
    { icon: DollarSign, label: "Laundered / GDP",     val: `${stats.money_laundered_pct_gdp}%`,     unit: "UNODC est.",   accent: CYAN   },
    { icon: Skull,      label: "Drug Users",          val: `${stats.drug_users_global_mn}M`,        unit: "worldwide 2024", accent: PAPER },
    { icon: Scale,      label: "Corruption Cost",     val: `$${stats.corruption_cost_usd_trn}T`,    unit: "annual",       accent: CRIMSON },
  ];

  const isActive = (moduleId) => lens === "all" || MODULE_LENS[moduleId] === lens;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10" data-testid="dashboard-page">
      <SEO title="Global Research Map" description="Live intelligence on organized crime, cybercrime, human trafficking, money laundering, and corruption — curated from UNODC, INTERPOL, FATF and IOCRI research units." path="/dashboard" image="institute" />

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 border border-gold/40 rounded-sm px-3 py-1 text-[10px] font-mono tracking-[0.36em] text-gold">
            <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
            GLOBAL RESEARCH MAP · LIVE
          </div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 text-white leading-[0.98] max-w-3xl">
            Every signal, geolocated.
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            Curated indicators from UNODC, INTERPOL, FATF, Europol, IMF and IOCRI research units. Choose a lens to focus the modules.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono tracking-[0.32em] text-white/40">DATASET</div>
          <div className="font-mono text-white/70 text-xs mt-1">GOCI 2023 · UNODC · FATF · Europol</div>
        </div>
      </div>

      {/* LENS SWITCH */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="dashboard-lens-switch">
        {LENSES.map((l) => (
          <button
            key={l.id}
            onClick={() => setLens(l.id)}
            data-testid={`lens-${l.id}`}
            className={`px-4 py-2 rounded-sm border font-mono text-[10px] tracking-[0.28em] transition-all ${
              lens === l.id
                ? "border-gold bg-gold/[0.08] text-gold"
                : "border-white/10 text-white/60 hover:border-gold/40 hover:text-white"
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* GLOBAL RESEARCH MAP (hero panel) */}
      <div className="relative rounded-sm border border-gold/25 bg-black/40 overflow-hidden mb-6" data-testid="dashboard-map-wrap">
        <div className="corner-brackets absolute inset-0 pointer-events-none" />
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
          <div className="flex items-center gap-3">
            <span className="evidence-chip">MAP · GOCI 2023</span>
            <span className="text-[10px] font-mono tracking-widest text-white/50">TOP-10 CRIMINALITY SCORES</span>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[10px] font-mono tracking-widest text-gold/70">
            <Radio className="h-3 w-3" /> STREAMING
          </div>
        </div>
        <MapView points={HOTSPOTS} height={460} zoom={2} />
      </div>

      {/* KPI STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-8">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="rounded-sm border border-white/10 bg-black/50 p-4 hover:border-gold/40 transition-colors"
            data-testid={`kpi-${k.label.replace(/\s+/g, "-")}`}
            style={{ boxShadow: `inset 3px 0 0 0 ${k.accent}` }}
          >
            <div className="flex items-center justify-between">
              <k.icon className="h-4 w-4" style={{ color: k.accent }} />
              <ArrowUpRight className="h-3 w-3 text-white/30" />
            </div>
            <div className="text-[9px] font-mono tracking-[0.32em] text-white/50 mt-3">{k.label.toUpperCase()}</div>
            <div className="font-display text-2xl md:text-3xl text-white mt-1 leading-none">{k.val}</div>
            {k.unit && <div className="text-[9px] text-white/40 mt-1 font-mono tracking-widest">{k.unit}</div>}
          </div>
        ))}
      </div>

      {/* DEPTH MODULES — dossier cards with corner brackets */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Cybercrime cost trend */}
        <ModulePanel
          id="cybercrime-trend"
          chip="TREND · CYBERCRIME"
          title="Global cybercrime cost, 2019 — 2026"
          citation="Cybersecurity Ventures · IOCRI research"
          active={isActive("cybercrime-trend")}
        >
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={stats.trend_cybercrime}>
              <defs>
                <linearGradient id="cyanFade" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor={CYAN} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={CYAN} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="year" {...AXIS_STYLE} />
              <YAxis {...AXIS_STYLE} unit="T" />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: CYAN, strokeOpacity: 0.35 }} />
              <Area type="monotone" dataKey="cost" stroke={CYAN} strokeWidth={2} fill="url(#cyanFade)" dot={{ fill: CYAN, r: 3 }} activeDot={{ r: 5, fill: CYAN }} />
            </AreaChart>
          </ResponsiveContainer>
        </ModulePanel>

        {/* Regional index */}
        <ModulePanel
          id="regions"
          chip="INDEX · REGIONAL"
          title="Organized Crime Index by region"
          citation="Global Organized Crime Index 2023"
          active={isActive("regions")}
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.regions}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="region" {...AXIS_STYLE} />
              <YAxis {...AXIS_STYLE} domain={[0, 10]} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(0,212,255,0.06)" }} />
              <Bar dataKey="score" radius={[2, 2, 0, 0]}>
                {stats.regions.map((r, i) => (
                  <Cell key={i} fill={r.score >= 5.2 ? CRIMSON : CYAN} fillOpacity={0.85} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ModulePanel>

        {/* Trafficking forms */}
        <ModulePanel
          id="traffic-forms"
          chip="TRAFFICKING · FORMS"
          title="Trafficking victims by form"
          citation="UNODC · GLOTIP"
          active={isActive("traffic-forms")}
        >
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={stats.trafficking_by_form}
                dataKey="pct"
                nameKey="form"
                innerRadius={68}
                outerRadius={104}
                paddingAngle={2}
                stroke="#080B0F"
                strokeWidth={2}
              >
                {stats.trafficking_by_form.map((_, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
              </Pie>
              <Legend wrapperStyle={{ color: "#84929F", fontFamily: "'JetBrains Mono'", fontSize: 10, letterSpacing: "0.2em" }} iconType="square" />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </ModulePanel>

        {/* Laundering typologies */}
        <ModulePanel
          id="laundering"
          chip="AML · TYPOLOGIES"
          title="Money-laundering methods, 2024"
          citation="FATF · Egmont · IOCRI research"
          active={isActive("laundering")}
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.money_laundering_typologies} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis type="number" {...AXIS_STYLE} unit="%" />
              <YAxis type="category" dataKey="type" {...AXIS_STYLE} width={130} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(0,212,255,0.06)" }} />
              <Bar dataKey="share" fill={CYAN} radius={[0, 2, 2, 0]} fillOpacity={0.85} />
            </BarChart>
          </ResponsiveContainer>
        </ModulePanel>
      </div>

      {/* TRENDING TOPICS */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <span className="evidence-chip">TRENDING · IOCRI SIGNAL</span>
          <span className="text-[10px] font-mono tracking-widest text-white/40">UPDATED WEEKLY</span>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            "#DeepfakeFraud", "#CryptoMixers", "#SextortionRings",
            "#DrugPrecursors", "#WildlifeTrade", "#SanctionsEvasion",
          ].map((t, i) => (
            <div
              key={t}
              className="rounded-sm border border-white/10 bg-black/40 hover:border-gold/40 transition-colors p-4"
              data-testid={`trending-${i}`}
            >
              <div className="text-[9px] font-mono tracking-widest text-white/40">TREND · {String(i + 1).padStart(2, "0")}</div>
              <div className="font-display text-lg text-white mt-2">{t}</div>
              <div className="mt-3 h-0.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${40 + (i * 10)}%`, background: i % 2 === 0 ? CYAN : CRIMSON }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModulePanel({ id, chip, title, citation, active, children }) {
  return (
    <div
      className={`corner-brackets relative rounded-sm border bg-black/40 transition-all ${
        active ? "border-gold/40" : "border-white/5 opacity-45 hover:opacity-70"
      }`}
      data-testid={`module-${id}`}
    >
      {/* Header stripe */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
        <div className="flex items-center gap-3">
          <span className="evidence-chip">{chip}</span>
          <span className="text-white font-display text-sm md:text-base leading-tight">{title}</span>
        </div>
        {active && <span className="h-1.5 w-1.5 rounded-full bg-gold shadow-[0_0_8px_#00D4FF]" />}
      </div>
      <div className="p-4">
        {children}
      </div>
      <div className="px-5 py-2 border-t border-white/5 text-[9px] font-mono tracking-widest text-white/40">
        SOURCE · {citation.toUpperCase()}
      </div>
    </div>
  );
}
